import { useAppState } from 'context/AppState';
import { Progress, colors } from 'syngenta-digital-cropwise-react-ui-kit';
import { AgronomicItem, ImportanceItems, ItemsContainer } from './AgronomicWeightingsList.styles';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { AgroWeightingsTitle } from 'pages/RecommendationV2/V2Common.styles';

interface ArgonomicWeightingListItem {
  id: string;
  name: string;
  progress: number;
}

const normalizeText = (value?: string): string => value?.trim().toLowerCase() ?? '';

const isYieldWeighting = (item: ArgonomicWeightingListItem): boolean => {
  const normalizedName = normalizeText(item?.name);
  const normalizedId = normalizeText(item?.id);
  return normalizedName === 'yield' || normalizedId === 'yield';
};

const AgronomicWeightingsList = (): JSX.Element => {
  const [t] = useTranslation();
  const {
    apiData: { recommendationCropwise },
  } = useAppState();

  const [agronomicsItemsWithProgress, setAgronomicsItemsWithProgress] = useState<
    ArgonomicWeightingListItem[]
  >([]);

  useEffect(() => {
    if (!recommendationCropwise?.recommendations?.length) {
      setAgronomicsItemsWithProgress([]);
      return;
    }

    const agroWeights =
      recommendationCropwise?.recommendations[0]?.multi_field?.config?.agronomicWeights ?? [];
    if (!agroWeights.length) {
      setAgronomicsItemsWithProgress([]);
      return;
    }

    const isDDG = recommendationCropwise?.source === 'DDG';
    const generatedBy =
      recommendationCropwise?.recommendations?.[0]?.multi_field?.config?.agronomicInputs
        ?.generatedby;
    const isScriptGenerated = generatedBy?.toLowerCase?.() === 'script';
    // Hide yieldRanking from ag. weighting list as we want to promote the use of max yield as default
    const agronomicWeightingsData = agroWeights
      .filter((ag) => ag.Active && ag.Weight > 0 && (!isDDG || ag.Identifier !== 'yieldRanking'))
      .map((activeAg) => {
        const progress = activeAg?.Weight || 0;
        return {
          id: activeAg.Identifier,
          name: activeAg.Name,
          progress: progress,
        };
      });
    const shouldHideForScriptYield =
      isScriptGenerated &&
      agronomicWeightingsData.length === 1 &&
      isYieldWeighting(agronomicWeightingsData[0]);
    setAgronomicsItemsWithProgress(shouldHideForScriptYield ? [] : agronomicWeightingsData);
  }, [recommendationCropwise]);

  return (
    <>
      {agronomicsItemsWithProgress?.length > 0 && (
        <>
          <AgroWeightingsTitle>{t('Your agronomic weightings')}</AgroWeightingsTitle>
          <ItemsContainer data-testid="agronomic-weighting-section">
            {agronomicsItemsWithProgress.map((agItem) => (
              <AgronomicItem key={agItem.id}>
                <h1>{t(agItem.name)}</h1>
                <Progress
                  percent={agItem.progress}
                  showInfo={false}
                  strokeColor={colors.blue60}
                  trailColor={colors.neutral20}
                />
                <ImportanceItems>
                  <p>{t('Low importance')}</p>
                  <p>{t('High importance')}</p>
                </ImportanceItems>
              </AgronomicItem>
            ))}
          </ItemsContainer>
        </>
      )}
    </>
  );
};

export { AgronomicWeightingsList };
